/**
 * ClientCondominiumsReport Component
 * Shows condominiums per client report for SuperAdmin
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, HardDrive, RefreshCw, ChevronDown, ChevronUp, Archive, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_COLORS = {
  TRIAL: 'bg-yellow-100 text-yellow-800',
  BASIC: 'bg-blue-100 text-blue-800',
  PRO: 'bg-indigo-100 text-indigo-800'
};

const STATUS_COLORS = {
  ACTIVE: 'bg-green-100 text-green-800',
  ARCHIVED: 'bg-gray-100 text-gray-800',
  TRIAL: 'bg-yellow-100 text-yellow-800',
  SUSPENDED: 'bg-red-100 text-red-800'
};

export default function ClientCondominiumsReport() {
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState({});

  const fetchReport = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/client-condominiums-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const toggleClient = (workspaceId) => {
    setExpandedClients(prev => ({
      ...prev,
      [workspaceId]: !prev[workspaceId]
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card>
        <CardContent className="text-center py-8 text-muted-foreground">
          Errore nel caricamento del report
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Report Condomini per Cliente
            </CardTitle>
            <CardDescription>
              Riepilogo condomini creati da ogni cliente
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReport}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Totals Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-slate-700">{report.totals.workspaces}</div>
            <div className="text-xs text-slate-500">Clienti</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{report.totals.total_condominiums}</div>
            <div className="text-xs text-blue-600">Condomini Totali</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-700">{report.totals.active_condominiums}</div>
            <div className="text-xs text-green-600">Attivi</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-700">{report.totals.archived_condominiums}</div>
            <div className="text-xs text-gray-600">Archiviati</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-700">{report.totals.total_units}</div>
            <div className="text-xs text-purple-600">Unità Totali</div>
          </div>
          <div className="bg-indigo-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-indigo-700">{report.totals.total_storage_mb.toFixed(1)} MB</div>
            <div className="text-xs text-indigo-600">Storage Totale</div>
          </div>
        </div>

        {/* Client List */}
        <div className="space-y-2">
          {report.report.map((client) => {
            const isExpanded = expandedClients[client.workspace_id];
            const usagePercent = client.limits.max_condominiums > 0 
              ? (client.condominiums.total / client.limits.max_condominiums) * 100 
              : 0;
            
            return (
              <div key={client.workspace_id} className="border rounded-lg overflow-hidden">
                {/* Client Header */}
                <div 
                  className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
                  onClick={() => toggleClient(client.workspace_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      <div>
                        <div className="font-medium">{client.workspace_name}</div>
                        <div className="text-xs text-muted-foreground">{client.admin_email}</div>
                      </div>
                    </div>
                    <Badge className={PLAN_COLORS[client.plan_code] || 'bg-gray-100'}>
                      {client.plan_code}
                    </Badge>
                    <Badge className={STATUS_COLORS[client.plan_status] || 'bg-gray-100'} variant="outline">
                      {client.plan_status}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Quick Stats */}
                    <div className="flex items-center gap-1 text-sm">
                      <Building2 className="h-4 w-4 text-blue-500" />
                      <span className="font-medium">{client.condominiums.active}</span>
                      <span className="text-muted-foreground">/{client.limits.max_condominiums}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <Users className="h-4 w-4 text-purple-500" />
                      <span>{client.resources.units}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      <HardDrive className="h-4 w-4 text-indigo-500" />
                      <span>{client.resources.storage_mb.toFixed(1)} MB</span>
                    </div>
                    
                    {/* Usage indicator */}
                    <div className={`w-20 h-2 rounded-full overflow-hidden ${usagePercent >= 90 ? 'bg-red-100' : 'bg-slate-200'}`}>
                      <div 
                        className={`h-full ${usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(usagePercent, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content - Condominium List */}
                {isExpanded && (
                  <div className="p-3 border-t bg-white">
                    {client.condominiums.list.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        Nessun condominio creato
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-muted-foreground mb-2">
                          Condomini ({client.condominiums.total})
                        </div>
                        {client.condominiums.list.map((condo) => (
                          <div 
                            key={condo.id} 
                            className={`flex items-center justify-between p-2 rounded ${condo.status === 'ARCHIVED' ? 'bg-gray-50' : 'bg-blue-50'}`}
                          >
                            <div className="flex items-center gap-2">
                              {condo.status === 'ARCHIVED' ? (
                                <Archive className="h-4 w-4 text-gray-400" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              )}
                              <div>
                                <div className="font-medium text-sm">{condo.name}</div>
                                <div className="text-xs text-muted-foreground">{condo.city}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={condo.status === 'ARCHIVED' ? 'text-gray-500' : 'text-green-600'}>
                                {condo.status === 'ARCHIVED' ? 'Archiviato' : 'Attivo'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {condo.created_at ? new Date(condo.created_at).toLocaleDateString('it-IT') : '-'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Resource Details */}
                    <div className="mt-3 pt-3 border-t grid grid-cols-4 gap-2 text-xs text-muted-foreground">
                      <div>
                        <span className="font-medium">Unità:</span> {client.resources.units} / {client.limits.max_units}
                      </div>
                      <div>
                        <span className="font-medium">Documenti:</span> {client.resources.documents}
                      </div>
                      <div>
                        <span className="font-medium">Storage:</span> {client.resources.storage_mb.toFixed(2)} MB / {client.limits.max_storage_gb} GB
                      </div>
                      <div>
                        <span className="font-medium">Archiviati:</span> {client.condominiums.archived}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
